import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import sortBy from "lodash/sortBy";

import Loading from "ui/Loading";
import PageContent from "ui/PageContent";
import Panel from "ui/Panel";

import { useRoutes } from "utils/routes";
import { useMemberNewQuery } from "./gql/Query.generated";
import { useCreateOrganizationMembershipMutation } from "./gql/Mutation.generated";
import { useSetFlash, useSetNextFlashLocation } from "providers/WithFlashes";
import { useAppConfig } from "providers/WithAppConfig";
import MultiSelect from "ui/MultiSelect";

const MemberNew = () => {
  const { slug: organizationSlug } = useParams();
  const { organizationMember, organizationRoles } = useRoutes();
  const appConfig = useAppConfig();
  const [roles, setRoles] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const setFlash = useSetFlash();
  const setNextFlashLocation = useSetNextFlashLocation();
  const navigate = useNavigate();
  const { data, loading, error } = useMemberNewQuery({
    variables: { organizationSlug },
  });
  const [createOrganizationMembership] =
    useCreateOrganizationMembershipMutation();

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const roleOptions = sortBy(data.getOrganizationRoles, (k) => k.updatedAt).map(
    (sr) => {
      return {
        value: sr.id,
        label: sr.name,
      };
    },
  );

  const createMember = () => {
    if (inviteEmail === "") {
      setFlash("error", "E-Mail is required.");
      return;
    }
    if (roles.length === 0) {
      setFlash(
        "error",
        "You must select at least one role to assign to the new member.",
      );
      return;
    }
    createOrganizationMembership({
      variables: {
        organizationSlug: organizationSlug,
        inviteEmail: inviteEmail,
        roleIds: roles.map((r) => r.value),
      },
      onError: (error) => {
        setFlash("error", `Failed to send invite: ${error.message}`);
      },
      onCompleted: (data) => {
        const membership = data.createOrganizationMembership;
        const message = appConfig.emailIntegrationAvailable
          ? `Invite sent to "${membership.email}".`
          : "Member added - share the invite link to have them join the organization.";
        setFlash("success", message);
        setNextFlashLocation(
          organizationMember(organizationSlug, membership.id),
        );
        navigate(organizationMember(organizationSlug, membership.id));
      },
    });
  };

  return (
    <PageContent
      title={`Organization "${data.getOrganizationDetails.name}"`}
      pageCategory="organization-members"
      pageName="new"
      skipAutoFocus
    >
      <Panel title="Invite Organization Member">
        <div className="p-[10px] max-w-[600px]">
          <div className="mb-2">
            <strong>E-Mail</strong>
          </div>
          <div className="row mb-2">
            <div className="col-sm-6">
              <input
                type="email"
                className="form-control"
                required
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
          </div>
          <div className="mb-2">
            <strong>Assigned Roles</strong>
          </div>
          <div className="row mb-2">
            <div className="col-sm-6">
              <MultiSelect
                value={roles}
                options={roleOptions}
                onChange={(newRoles) => setRoles(newRoles)}
                isClearable={false}
              />
            </div>
            <div className="help-block col-sm-6">
              Select one or multiple roles to apply to this member. Each role
              may contain multiple permissions, check the{" "}
              <Link to={organizationRoles(organizationSlug)}>Roles page</Link>{" "}
              for details.
            </div>
          </div>
          <div>
            <button className="btn btn-success" onClick={createMember}>
              Send Invite
            </button>
          </div>
        </div>
      </Panel>
    </PageContent>
  );
};

export default MemberNew;
